// src/firebase/publicTradesService.js
// Scambi liberi resi pubblici: chiunque sia autenticato può vederli e votare
// (accetta/rifiuta) per 4 giorni. Non essendoci Cloud Functions in questo
// progetto, la chiusura del sondaggio è risolta lato client, in modo lazy:
// chi legge una proposta scaduta (il proprietario o chi la vota) la marca
// come chiusa. La UI tratta comunque un sondaggio come chiuso in base
// all'orario reale (closesAt), indipendentemente da quando questo flag viene
// scritto su Firestore.
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION = 'publicTrades';
export const TRADE_VOTE_DURATION_MS = 4 * 24 * 60 * 60 * 1000;

function tradesCollection() {
  return collection(db, COLLECTION);
}

function tradeDoc(tradeId) {
  return doc(db, COLLECTION, String(tradeId));
}

// Uno scambio è "aperto" per l'utente indipendentemente da quando/se qualcuno
// ha già scritto status:'closed' su Firestore: conta solo l'orario reale.
export function isTradeOpen(trade) {
  return trade?.status === 'open' && Date.now() < Number(trade?.closesAt || 0);
}

function computeResult(trade) {
  const accept = Number(trade?.acceptCount || 0);
  const reject = Number(trade?.rejectCount || 0);
  return accept > reject ? 'approved' : 'rejected';
}

// Chiude (lato client) le proposte scadute in una lista già caricata: scrive
// su Firestore solo quelle che risultano ancora 'open' oltre closesAt, e
// restituisce la stessa lista con lo stato aggiornato per il rendering
// immediato (senza aspettare la scrittura).
export async function resolveExpiredTrades(trades = []) {
  const now = Date.now();
  const updated = [];

  for (const trade of trades) {
    if (trade.status === 'open' && Number(trade.closesAt || 0) <= now) {
      const result = computeResult(trade);
      const patched = { ...trade, status: 'closed', result };
      updated.push(patched);
      updateDoc(tradeDoc(trade.id), { status: 'closed', result }).catch(() => {});
    } else {
      updated.push(trade);
    }
  }

  return updated;
}

export async function createPublicTrade({
  ownerUid,
  ownerDisplayName,
  offeredPlayers,
  receivedPlayers,
  analysis,
  kind = 'liberi',
  meta = null,
}) {
  if (!ownerUid) return null;

  const now = Date.now();
  const ref = await addDoc(tradesCollection(), {
    ownerUid,
    ownerDisplayName: ownerDisplayName || '',
    offeredPlayers: offeredPlayers || [],
    receivedPlayers: receivedPlayers || [],
    analysis: analysis || null,
    kind,
    meta,
    createdAt: now,
    closesAt: now + TRADE_VOTE_DURATION_MS,
    status: 'open',
    acceptCount: 0,
    rejectCount: 0,
    votesByUid: {},
    result: null,
    ownerSeenResult: false,
  });

  return ref.id;
}

// Elimina uno scambio pubblico proprio (aperto o già chiuso): le regole
// Firestore permettono la cancellazione solo al proprietario (ownerUid).
export async function deletePublicTrade(tradeId) {
  if (!tradeId) return;
  await deleteDoc(tradeDoc(tradeId));
}

// Conteggio veloce per la Home (statistica "Scambi pubblici"): non risolve i
// sondaggi scaduti (a differenza di getOpenPublicTrades), quindi può includere
// per breve tempo proposte già scadute ma non ancora chiuse da nessun client —
// accettabile per una statistica "a colpo d'occhio", si autocorregge appena
// qualcuno apre la pagina Scambi.
export async function getOpenPublicTradesCount() {
  const snap = await getCountFromServer(query(tradesCollection(), where('status', '==', 'open')));
  return snap.data().count;
}

export async function getOpenPublicTrades() {
  const snap = await getDocs(query(tradesCollection(), where('status', '==', 'open')));
  const trades = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  trades.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
  return resolveExpiredTrades(trades);
}

export async function getMyPublicTrades(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(tradesCollection(), where('ownerUid', '==', uid)));
  const trades = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  trades.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
  return resolveExpiredTrades(trades);
}

// Vota (o cambia voto) su uno scambio pubblico. Transazione per evitare corse
// tra più votanti contemporanei sugli stessi contatori.
export async function castVote(tradeId, uid, choice) {
  if (!tradeId || !uid || (choice !== 'accept' && choice !== 'reject')) return;

  await runTransaction(db, async (transaction) => {
    const ref = tradeDoc(tradeId);
    const snap = await transaction.get(ref);
    if (!snap.exists()) return;

    const data = snap.data();
    if (!isTradeOpen({ ...data, id: tradeId })) return;

    const votesByUid = { ...(data.votesByUid || {}) };
    const prevChoice = votesByUid[uid];
    if (prevChoice === choice) return;

    let acceptCount = Number(data.acceptCount || 0);
    let rejectCount = Number(data.rejectCount || 0);

    if (prevChoice === 'accept') acceptCount -= 1;
    if (prevChoice === 'reject') rejectCount -= 1;
    if (choice === 'accept') acceptCount += 1;
    if (choice === 'reject') rejectCount += 1;

    votesByUid[uid] = choice;

    transaction.update(ref, {
      votesByUid,
      acceptCount: Math.max(0, acceptCount),
      rejectCount: Math.max(0, rejectCount),
    });
  });
}

export async function markTradeResultSeen(tradeId) {
  if (!tradeId) return;
  await updateDoc(tradeDoc(tradeId), { ownerSeenResult: true }).catch(() => {});
}

// Conteggio per il badge in TopBar: risolve prima le proprie proposte scadute
// (così lo stato su Firestore resta aggiornato), poi conta quelle chiuse e
// non ancora viste dal proprietario.
export async function getAndResolveMyTradesBadgeCount(uid) {
  if (!uid) return 0;

  const mySnap = await getDocs(
    query(tradesCollection(), where('ownerUid', '==', uid), where('status', '==', 'open'))
  );
  const myOpenTrades = mySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  await resolveExpiredTrades(myOpenTrades);

  const closedSnap = await getDocs(
    query(
      tradesCollection(),
      where('ownerUid', '==', uid),
      where('status', '==', 'closed'),
      where('ownerSeenResult', '==', false)
    )
  );

  return closedSnap.size;
}

export async function getPublicTradeById(tradeId) {
  if (!tradeId) return null;
  const snap = await getDoc(tradeDoc(tradeId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}
